import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Flame,
  Users,
  Music2,
  Share2,
  Briefcase,
  Cpu,
  Sparkles,
  Clapperboard,
  MapPin,
  TrendingUp,
  ArrowRight,
  Trophy,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { categoriesQuery, boardsOverviewQuery } from "@/lib/queries";
import { formatHeat } from "@/lib/behot";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import categoriesHero from "@/assets/categories-hero.jpg";
import { CATEGORIES_OG_IMAGE, CATEGORIES_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";
import { proxiedImage } from "@/lib/remote-image";

export const Route = createFileRoute("/categories")({
  // Prefetched on the server so every board link is a crawlable anchor in
  // the initial HTML instead of appearing only after client hydration.
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(categoriesQuery),
      context.queryClient.ensureQueryData(boardsOverviewQuery),
    ]);
  },
  head: () =>

    buildPageMetadata({
      path: "/categories",
      title: "All Categories — Live Ranking Boards | BeHot.Now",
      description:
        "Browse every BeHot.Now ranking board: creators, DJs, artists, influencers, AI tools, startups, apps, brands and products. Live Heat, listing counts and entry price.",
      keywords: [
        "ranking categories",
        "music charts",
        "startup charts",
        "creator categories",
        "top apps board",
        "trending brands",
      ],
      ogTitle: "All ranking boards on BeHot.Now",
      ogDescription: "Live Heat, listing counts and entry price for every category board.",
      image: CATEGORIES_OG_IMAGE,
      imageAlt: CATEGORIES_OG_IMAGE_ALT,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Categories", path: "/categories" },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "All categories",
        url: `${SITE_URL}/categories`,
      },
    }),
  component: CategoriesPage,
});

const ART: Record<string, { icon: LucideIcon; tint: string }> = {
  creators: { icon: Users, tint: "from-primary/25 via-primary/10 to-transparent" },
  music: { icon: Music2, tint: "from-creative/25 via-creative/10 to-transparent" },
  social: { icon: Share2, tint: "from-signal/25 via-signal/10 to-transparent" },
  business: { icon: Briefcase, tint: "from-primary/20 via-signal/10 to-transparent" },
  tech: { icon: Cpu, tint: "from-signal/20 via-creative/10 to-transparent" },
  brands: { icon: Sparkles, tint: "from-creative/20 via-primary/10 to-transparent" },
  entertainment: { icon: Clapperboard, tint: "from-primary/20 via-creative/10 to-transparent" },
  local: { icon: MapPin, tint: "from-success/20 via-signal/10 to-transparent" },
};

function artFor(slug: string, name: string) {
  const key = Object.keys(ART).find((k) => slug.includes(k) || name.toLowerCase().includes(k));
  return ART[key ?? "creators"]!;
}

function CategoriesPage() {
  const { t } = useT();
  const { data: categories = [] } = useQuery(categoriesQuery);
  const { data: overview } = useQuery(boardsOverviewQuery);
  const parents = categories.filter((c) => !c.parent_id);

  const totalHeat = [...(overview?.values() ?? [])].reduce((a, b) => a + b.heat, 0);
  const totalListings = [...(overview?.values() ?? [])].reduce((a, b) => a + b.listings, 0);

  const stat = (id: string) => overview?.get(id);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <section className="card-surface relative overflow-hidden p-0">
        <div className="grid items-center gap-6 md:grid-cols-[1.15fr_1fr]">
          <div className="p-8 sm:p-10">
            <Badge variant="secondary" className="uppercase tracking-widest">
              {t("boards.categories.boardsCount", { n: parents.length })} · {t("boards.categories.subboardsCount", { n: categories.length - parents.length })}
            </Badge>
            <h1 className="mt-4 font-display text-4xl font-bold sm:text-5xl">
              {t("boards.categories.heroTitlePrefix")} <span className="text-gradient-hot">{t("boards.categories.heroTitleHighlight")}</span>
            </h1>
            <p className="mt-3 max-w-lg text-muted-foreground">
              {t("boards.categories.heroSubtitle")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild variant="hot">
                <Link to="/get-hot" search={{ listing: undefined, category: undefined }}>
                  <Flame className="size-4" /> {t("boards.categories.getOnBoard")}
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/how-it-works">{t("boards.categories.howRankingWorks")}</Link>
              </Button>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4 border-t border-border pt-6">
              <div>
                <p className="font-display text-2xl font-bold">{formatHeat(totalHeat)}</p>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("boards.categories.liveHeat")}</p>
              </div>
              <div>
                <p className="font-display text-2xl font-bold">{totalListings}</p>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("boards.categories.ranked")}</p>
              </div>
              <div>
                <p className="font-display text-2xl font-bold">{categories.length}</p>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("boards.categories.boards")}</p>
              </div>
            </div>
          </div>
          <img
            src={categoriesHero}
            alt={t("boards.categories.heroImageAlt")}
            width={1600}
            height={900}
            className="h-full w-full object-cover"
          />
        </div>
      </section>

      <div className="mt-10 grid gap-5 md:grid-cols-2">
        {parents.map((parent) => {
          const children = categories.filter((c) => c.parent_id === parent.id);
          const art = artFor(parent.slug, parent.name);
          const Icon = art.icon;
          const own = stat(parent.id);
          const heat =
            (own?.heat ?? 0) + children.reduce((a, c) => a + (stat(c.id)?.heat ?? 0), 0);
          const listings =
            (own?.listings ?? 0) + children.reduce((a, c) => a + (stat(c.id)?.listings ?? 0), 0);
          const floors = [own, ...children.map((c) => stat(c.id))]
            .filter((o) => o && o.listings > 0)
            .map((o) => o!.entryFloor);
          const entryFloor = floors.length ? Math.min(...floors) : 0;
          const top = [own, ...children.map((c) => stat(c.id))]
            .flatMap((o) => o?.top ?? [])
            .sort((a, b) => b.heat - a.heat)
            .filter(
              (entry, i, arr) => arr.findIndex((x) => x.listing.id === entry.listing.id) === i,
            )
            .slice(0, 4);

          return (
            <section key={parent.id} className="card-surface overflow-hidden p-0">
              <div className={`relative bg-gradient-to-br ${art.tint} p-6`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="grid size-12 place-items-center rounded-2xl bg-card shadow-sm">
                      <Icon className="size-6 text-primary" />
                    </span>
                    <div>
                      <Link
                        to="/c/$slug"
                        params={{ slug: parent.slug }}
                        className="font-display text-xl font-bold hover:text-primary"
                      >
                        {parent.name}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        {t("boards.categories.subboardsListings", { n: children.length, n2: listings })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="flex items-center gap-1 font-display text-lg font-bold">
                      <Flame className="size-4 text-primary" />
                      {formatHeat(heat)}
                    </p>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {t("boards.categories.liveHeatLower")}
                    </p>
                  </div>
                </div>

                {top.length > 0 && (
                  <div className="mt-5 flex items-center gap-3">
                    <Trophy className="size-4 shrink-0 text-signal" />
                    <div className="flex -space-x-2">
                      {top.map(({ listing }) =>
                        listing.logo_url ? (
                          <img
                            key={listing.id}
                            src={proxiedImage(listing.logo_url)}
                            alt={listing.name}
                            loading="lazy"
                            width={32}
                            height={32}
                            className="size-8 rounded-full border-2 border-card bg-card object-cover"
                          />
                        ) : (
                          <span
                            key={listing.id}
                            className="grid size-8 place-items-center rounded-full border-2 border-card bg-muted text-[11px] font-bold"
                          >
                            {listing.name.slice(0, 1)}
                          </span>
                        ),
                      )}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {t("boards.categories.leading")} <span className="font-medium text-foreground">{top[0]!.listing.name}</span>
                    </p>
                  </div>
                )}
              </div>

              <div className="p-6 pt-5">
                {children.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {children.map((child) => {
                      const s = stat(child.id);
                      return (
                        <Link
                          key={child.id}
                          to="/c/$slug"
                          params={{ slug: child.slug }}
                          className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
                        >
                          {child.name}
                          {s ? (
                            <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-primary">
                              <Flame className="size-3" />
                              {formatHeat(s.heat)}
                            </span>
                          ) : (
                            <span className="text-[11px] text-muted-foreground/70">{t("boards.categories.open")}</span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}

                <div className="mt-5 flex items-center justify-between gap-3 border-t border-border pt-4">
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <TrendingUp className="size-3.5 text-success" />
                    {listings > 0
                      ? t("boards.categories.entryFrom", { amount: formatHeat(entryFloor) })
                      : t("boards.categories.noOneRanked")}
                  </p>
                  <Button asChild size="sm" variant="ghost">
                    <Link to="/c/$slug" params={{ slug: parent.slug }}>
                      {t("boards.categories.viewBoard")} <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <section id="all-boards" className="mt-12">
        <h2 className="font-display text-2xl font-bold">
          {t("boards.categories.boards")} · {categories.length}
        </h2>
        <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {parents.map((parent) => {
            const children = categories.filter((c) => c.parent_id === parent.id);
            return (
              <div key={parent.id} className="card-surface p-5">
                <Link
                  to="/c/$slug"
                  params={{ slug: parent.slug }}
                  className="font-display text-base font-bold hover:text-primary"
                >
                  {parent.name}
                </Link>
                <ul className="mt-3 space-y-1.5">
                  {children.map((child) => {
                    const s = stat(child.id);
                    return (
                      <li key={child.id} className="flex items-center justify-between gap-2 text-sm">
                        <Link
                          to="/c/$slug"
                          params={{ slug: child.slug }}
                          className="truncate text-muted-foreground hover:text-foreground"
                        >
                          {child.name}
                        </Link>
                        <span className="inline-flex shrink-0 items-center gap-0.5 text-[11px] font-semibold text-primary">
                          <Flame className="size-3" />
                          {formatHeat(s?.heat ?? 0)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </section>
    </div>

  );
}
